import { Add, Cached } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { ApplicationUser } from 'api/interfaces/applicationUser';
import { User } from 'auth/user';
import { CenteredModal } from 'components/CenteredModal';
import { FormModal } from 'components/FormModal';
import { QuestionModal } from 'components/QuestionModal';
import { Scaffold } from 'components/Scaffold';
import { Table, TableMode } from 'components/Table';
import { RowData } from 'components/Table/types/rowData';
import { TaskRunningStatus } from 'components/TaskRunningStatus';
import { CRUDActionsContext } from 'contexts/CRUDActionsContext';
import { useApi } from 'hooks/useApi';
import { useCRUD } from 'hooks/useCRUD';
import { useTable } from 'hooks/useTable';
import React from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { closeTaskModal } from 'redux/actions/users/closeTaskModal';
import { createUser } from 'redux/actions/users/createUser';
import { deleteUser } from 'redux/actions/users/deleteUser';
import { getUsers } from 'redux/actions/users/getUsers';
import { updateUser } from 'redux/actions/users/updateUser';
import { userScreenUpdate } from 'redux/actions/users/userScreenUpdate';
import { initializeUsersForm } from 'redux/actions/usersForm/initializeUsersForm';
import { ApplicationState } from 'redux/applicationState';
import { UsersState } from 'redux/reducers/usersReducer';
import styles from 'routes/EntityManagement/FirmsManagement/firms-management.module.scss';
import { createUsersColumns } from 'routes/EntityManagement/UsersManagement/columns';
import UsersForm from 'routes/EntityManagement/UsersManagement/components/UsersForm';
import { SignalR, SignalRContext } from 'signalR';

interface DispatchProps {
  closeTaskModal(): void;

  createUser(user: ApplicationUser): any;
  updateUser(user: ApplicationUser): any;
  deleteUser(user: ApplicationUser): any;
  getUsers(): any;

  initializeUsersForm(): any;
  userScreenUpdate(userScreenData: any): any;
}

interface StateProps extends UsersState {
  readonly currentUser: User;
}

type Props = StateProps & DispatchProps;

const UsersManagement: React.FC<Props> = (props: Props): React.ReactElement => {
  const { users, loading, currentUser: loggedInUser } = props;
  const {
    closeTaskModal,
    createUser,
    updateUser,
    deleteUser,
    getUsers,
    initializeUsersForm,
    userScreenUpdate,
  } = props;
  const [crud, mode, selectedUser] = useCRUD(users, emptyUser);
  const currentRightsUser = loggedInUser;
  const canEditUsers = React.useMemo(
    (): boolean => currentRightsUser.hasAnyRight('WRITE_USERS'),
    [currentRightsUser]
  );
  const canDeleteUsers = React.useMemo(
    (): boolean => currentRightsUser.hasAnyRight('DELETE_USERS'),
    [currentRightsUser]
  );
  const canViewUsers = React.useMemo(
    (): boolean => currentRightsUser.hasAnyRight('VIEW_USERS', 'WRITE_USERS', 'DELETE_USERS'),
    [currentRightsUser]
  );
  const cancelTaskFnRef = React.useRef<VoidFunction>(null);
  const signalR = React.useContext<SignalR>(SignalRContext);
  const columnDefinitions = React.useMemo(
    () => createUsersColumns({ canEdit: canEditUsers, canDelete: canDeleteUsers }),
    [canEditUsers, canDeleteUsers]
  );
  const tableProps = useTable('users-tool', columnDefinitions);
  // const mode = columnData.mode;
  const closeModal = React.useCallback((): void => {
    crud.reset();
  }, [crud]);
  const api = useApi();

  // Handle user screen updates from SignalR
  const handleUserScreenUpdate = React.useCallback(
    (event: Event): void => {
      const customEvent = event as CustomEvent<any>;
      const userScreenData = customEvent.detail;

      userScreenUpdate(userScreenData);
    },
    [userScreenUpdate]
  );

  // Subscribe to SignalR user screen updates
  React.useEffect((): VoidFunction => {
    if (signalR) {
      try {
        signalR.subscribeForUserScreenUpdates();
        document.addEventListener('onUserScreenUpdate', handleUserScreenUpdate);
      } catch (error) {
        console.error('Failed to subscribe to user screen updates:', error);
      }
    }

    return (): void => {
      if (signalR) {
        try {
          signalR.unsubscribeForUserScreenUpdates();
          document.removeEventListener('onUserScreenUpdate', handleUserScreenUpdate);
        } catch (error) {
          console.error('Failed to unsubscribe from user screen updates:', error);
        }
      }
    };
  }, [signalR, handleUserScreenUpdate]);

  const rows = React.useMemo((): Record<string, RowData<ApplicationUser>> => {
    return users.reduce(
      (
        newData: Record<string, RowData<ApplicationUser>>,
        systemUser: ApplicationUser
      ): Record<string, RowData<ApplicationUser>> => {
        return {
          ...newData,
          [systemUser.email]: {
            id: systemUser.email,
            data: systemUser,
          },
        };
      },
      {}
    );
  }, [users]);

  const save = React.useCallback(
    (user: ApplicationUser): void => {
      if (!canEditUsers) {
        return;
      }

      switch (mode) {
        case TableMode.create:
          cancelTaskFnRef.current = createUser(user);
          break;
        case TableMode.edit:
          cancelTaskFnRef.current = updateUser(user);
          break;
      }
    },
    [createUser, mode, updateUser]
  );

  const remove = React.useCallback((): void => {
    if (!canDeleteUsers) {
      return;
    }

    deleteUser(selectedUser);
  }, [canDeleteUsers, deleteUser, selectedUser]);

  const onTaskErrored = React.useCallback((): void => {
    closeTaskModal();
  }, [closeTaskModal]);

  const onTaskCompleted = React.useCallback((): void => {
    closeTaskModal();
    closeModal();
  }, [closeModal, closeTaskModal]);

  const refresh = React.useCallback((): void => {
    api.refreshUserGroupCache();
  }, [api]);

  React.useEffect((): VoidFunction => {
    if (!canViewUsers) {
      return (): void => undefined;
    }

    const cancel1 = initializeUsersForm();
    const cancel2 = getUsers();

    return (): void => {
      cancel1();
      cancel2();
    };
  }, [canViewUsers, getUsers, initializeUsersForm]);

  if (!canViewUsers) {
    return (
      <Scaffold title="Users Tool">
        <Box padding={3}>You do not have permission to view users.</Box>
      </Scaffold>
    );
  }

  return (
    <CRUDActionsContext.Provider value={crud}>
      <Scaffold title="Users Tool">
        <Box className={styles.header}>
          <Button onClick={crud.add} disabled={!canEditUsers}>
            <Add /> Add New
          </Button>
          <Button onClick={refresh}>
            <Cached />
            REFRESH
          </Button>
        </Box>
        <Table rowKey="email" rows={rows} loading={loading} {...tableProps} />
        <FormModal
          entityName="User"
          mode={mode}
          open={mode === TableMode.edit || mode === TableMode.create}
          onClose={closeModal}
        >
          <UsersForm email={selectedUser.email} onSave={save} />
        </FormModal>
        <CenteredModal title="Delete User" open={mode === TableMode.remove} onClose={closeModal}>
          <QuestionModal
            title="Are you sure?"
            message={`You are going to delete ${selectedUser.email}`}
            onYes={remove}
            onNo={closeModal}
          />
        </CenteredModal>
        <TaskRunningStatus
          modalType={props.modalType}
          message={props.taskMessage}
          onErrored={onTaskErrored}
          onSucceeded={onTaskCompleted}
          onCancel={cancelTaskFnRef.current}
        />
      </Scaffold>
    </CRUDActionsContext.Provider>
  );
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, any> = {
  createUser,
  updateUser,
  deleteUser,
  getUsers,
  closeTaskModal,
  initializeUsersForm,
  userScreenUpdate,
};

const mapStateToProps: MapStateToProps<StateProps, any, ApplicationState> = (
  applicationState: ApplicationState
): StateProps => ({
  ...applicationState.users,
  currentUser: applicationState.currentUser.user,
});

export default connect(mapStateToProps, mapDispatchToProps)(UsersManagement);

const emptyUser = ApplicationUser.defaultUser();
